// src/layouts/calendar/CalendarPcLayout.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "../../pages/CalendarGeneral.module.css";
import api from "../../api";
import { useLocation } from "react-router-dom";

// FullCalendar core & plugins
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import multiMonthPlugin from "@fullcalendar/multimonth";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";

// Components
import CalendarSidebar from "../../components/calendar/CalendarSidebar";
import { EVENT_TYPE_COLORS } from "../../constants/eventTypes";
import EventCreateModal from "../../components/EventCreateModal";
import EventDetailModal from "../../components/EventDetailModal";
import { withEndedLabel } from "../../utils/contractEnded";
import { rangeFromSelection } from "../../utils/leavePrefill";
import CalendarSearch from "../../components/calendar/CalendarSearch";

/* ==============================
   일정 유형별 색상
============================== */
// 유형·색은 constants/eventTypes.js 단일 출처에서 온다(사이드바·모바일과 공유).
const eventColors = EVENT_TYPE_COLORS;

// ✅ 휴가 일정: 원본 시간(start/end/break)으로 "X시간 Y분 사용" 재생성
function formatLeaveTitle(raw) {
  const labelMap = { 연차: "연차", 반차: "반차", 대체휴무: "대휴", 특별휴가: "특휴" };
  const label = labelMap[raw.event_type];
  // 휴가가 아니면 원래 제목 그대로
  if (!label) return raw.title || "";

  try {
    const [sh, sm] = raw.start_time.split(":").map(Number);
    const [eh, em] = raw.end_time.split(":").map(Number);
    let minutes = (eh * 60 + em) - (sh * 60 + sm) - Math.round((raw.break_hours || 0) * 60);
    if (minutes < 0) minutes = 0;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    let timeStr;
    if (h && m) timeStr = `${h}시간 ${m}분`;
    else if (h) timeStr = `${h}시간`;
    else timeStr = `${m}분`;
    return `${label} ${timeStr} 사용`;
  } catch {
    return raw.title || "";
  }
}

/* ==============================
   FullCalendar 일정 표시 커스터마이징
============================== */
function renderEventContent(eventInfo) {
  // ✅ 공휴일: 공지사항 행에 빨간 텍스트로 표시
  if (eventInfo.event.extendedProps.isHoliday) {
    return (
      <div style={{
        color: "#d32f2f",
        fontWeight: 700,
        fontSize: "12px",
        padding: "1px 4px",
        backgroundColor: "transparent",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}>
        {eventInfo.event.title}
      </div>
    );
  }

  const raw = eventInfo.event.extendedProps.raw || {};
  const assignee = raw.assignee_display || "";
  const description = raw.description || "";
  const title = formatLeaveTitle(raw);

  const start = eventInfo.event.start
    ? eventInfo.event.start.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "";
  const end = eventInfo.event.end
    ? eventInfo.event.end.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "";

  const color = eventInfo.event.extendedProps.color || "#e0e0e0";
  const currentView = eventInfo.view.type;

  const outerStyle = {
    height: "100%",
    backgroundColor: color,
    borderRadius: "4px",
    display: "flex",
    alignItems: "flex-start",
    padding: "2px",
  };

  const innerStyle = {
    display: "inline-block",
    backgroundColor: "transparent",
    color: "black",
    padding: "2px 4px",
    whiteSpace: "nowrap",
    maxWidth: "fit-content",
  };

  if (currentView === "timeGridDay" || currentView === "resourceTimeGridDay") {
    return (
      <div style={outerStyle}>
        <div style={innerStyle}>
          {assignee && (
            <div style={{
              fontWeight: "bold",
              fontSize: "1.1em",
              lineHeight: "1.2",
            }}>
              {assignee}
            </div>
          )}
          {start && end && <div>{start} ~ {end}</div>}
          <div>제목: {title}</div>
          {description && <div>설명: {description}</div>}
        </div>
      </div>
    );
  }

  if (currentView === "timeGridWeek") {
    return (
      <div style={outerStyle}>
        <div style={innerStyle}>
          {assignee && <div style={{ fontWeight: "bold" }}>{assignee}</div>}
          {start && end && <div>{start} ~ {end}</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={outerStyle}>
      <div style={innerStyle}>
        {assignee && <span style={{ fontWeight: "bold" }}>{assignee}</span>}
        {start && <span> {start}</span>}
      </div>
    </div>
  );
}

/* ==============================
   CalendarPcLayout 본문
============================== */
export default function CalendarPcLayout() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState("resourceTimeGridDay");

  const [serverEvents, setServerEvents] = useState([]);
  const [filters, setFilters] = useState({
    // 전체 유형 기본 체크 — 색상표(단일 출처)에서 만들어 유형 추가 시 자동 반영된다.
    // 손으로 나열하면 새 유형이 기본 꺼짐이 돼 "만들었는데 캘린더에 안 보인다"가 된다.
    types: Object.fromEntries(Object.keys(EVENT_TYPE_COLORS).map((t) => [t, true])),
    personalTypes: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  // 드래그로 고른 범위(2026-10-19) — 등록 창과 휴가신청서 바로가기 프리필에 쓴다. 버튼·단축키로 열면 null.
  const [createRange, setCreateRange] = useState(null);
  const openCreate = (range = null) => {
    setCreateRange(range);
    setShowCreateModal(true);
  };
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const calRef = useRef(null);

  const [allUsers, setAllUsers] = useState([]);
  const [holidays, setHolidays] = useState([]);

  // ✅ 일정 검색 — 고른 일정 강조 + 검색 전 화면 복귀용
  const [highlightId, setHighlightId] = useState(null);
  const preSearchRef = useRef(null);   // { date, view } — 첫 결과 클릭 때만 저장


  useEffect(() => {
    api.get("/users/users/").then((res) => {
      const raw = res.data?.results ?? (Array.isArray(res.data) ? res.data : []);
      const list = raw
        .filter((u) => u.employee_type !== "retired" && u.approved === true && u.name)
        .sort((a, b) => (a.position_rank ?? 99) - (b.position_rank ?? 99));
      setAllUsers(list);
    }).catch((err) => console.error("직원 목록 불러오기 실패", err));
  }, []);

  // ✅ 연도(year)만 의존 → 같은 해 안에서는 다시 안 부름 (깜빡임 방지)
  const currentYear = currentDate.getFullYear();
  useEffect(() => {
    let cancelled = false;
    api.get(`/calendar/holidays/?year=${currentYear}`)
      .then((res) => {
        if (!cancelled) setHolidays(res.data || []);
      })
      .catch((err) => console.error("공휴일 불러오기 실패", err));
    return () => { cancelled = true; };
  }, [currentYear]);



  const resources = useMemo(() => {
    const namesInEvents = new Set(
      serverEvents.map((ev) => ev.assignee_display || ev.author_display).filter(Boolean)
    );
    return allUsers
      .filter((u) => namesInEvents.has(`${u.name} ${u.position}`))
      .map((u) => ({
        // ⚠️ id는 일정의 assignee_display와 맞추는 매칭 키라 원본 그대로 — 표기는 title에만.
        id: `${u.name} ${u.position}`,
        title: withEndedLabel(`${u.name} ${u.position}`, u.is_contract_ended),
        ended_order: u.is_contract_ended ? 1 : 0,   // 계약종료자 맨 아래(resourceOrder 1순위)
        position_rank: u.position_rank ?? 99,
      }));
  }, [allUsers, serverEvents]);

  /* ==============================
      view별 날짜 범위 계산
  ============================== */
  const range = useMemo(() => {
    const d = currentDate;
    const y = d.getFullYear();
    const m = d.getMonth();
    const pad = (n) => String(n).padStart(2, "0");
    const iso = (date) =>
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

    if (view === "timeGridDay" || view === "resourceTimeGridDay") {
      const start = new Date(y, m, d.getDate());
      return { start: iso(start), end: iso(start) };
    }
    if (view === "timeGridWeek") {
      const day = d.getDay();
      const start = new Date(y, m, d.getDate() - day);
      const end = new Date(y, m, d.getDate() - day + 6);
      return { start: iso(start), end: iso(end) };
    }
    if (view === "dayGridMonth") {
      const start = new Date(y, m, 1);
      const end = new Date(y, m + 1, 0);
      return { start: iso(start), end: iso(end) };
    }
    if (view === "multiMonthYear") {
      const start = new Date(y, 0, 1);
      const end = new Date(y, 11, 31);
      return { start: iso(start), end: iso(end) };
    }
    return { start: iso(d), end: iso(d) };
  }, [currentDate, view]);

  /* ==============================
      일정 불러오기
  ============================== */
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/calendar/events/", {
        params: { start: range.start, end: range.end },
      });

      const data = res.data || [];

      const filtered = data.filter((ev) => {
        const eventType = ev.event_type || "업무일정";
        const typeOk = filters.types[eventType] ?? true;
        const personName = ev.assignee_display || ev.author_display || "";
        const matchedKey = Object.keys(filters.personalTypes).find(
          (key) => key.includes(personName) || personName.includes(key)
        );
        const personalOk = matchedKey
          ? filters.personalTypes[matchedKey]?.[eventType] ?? false
          : false;
        // 검색으로 고른 일정은 사이드바 체크와 무관하게 보이게 한다(그 한 건만)
        return typeOk || personalOk || (highlightId != null && ev.id === highlightId);
      });

      setServerEvents(filtered);
    } catch (err) {
      console.error(err);
      setError("❌ 일정을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [range, filters, highlightId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  /* ==============================
      ✅ FullCalendar와 view 상태 동기화
  ============================== */
  useEffect(() => {
    const api = calRef.current?.getApi?.();
    if (!api) return;
    api.changeView(view);
  }, [view]);

  /* ==============================
      ⌨️ 단축키 설정 (키보드 단축키)
  ============================== */
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      const isTyping = ["input", "textarea", "select"].includes(tag);
    
      if (e.ctrlKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        openCreate();
        console.log("📅 단축키: 새 일정 만들기 (Ctrl + N)");
      }
      if (!e.ctrlKey && e.key.toLowerCase() === "t" && !isTyping) {
        e.preventDefault();
        handleToday();
        console.log("📅 단축키: 오늘로 이동 (T)");
      }

      if (e.key === "ArrowRight" && !isTyping) {
        e.preventDefault();
        handleNext();
      }
      if (e.key === "ArrowLeft" && !isTyping) {
        e.preventDefault();
        handlePrev();
      }

      if (isTyping) return;
      const k = e.key.toLowerCase();
      if (k === "d") setView("resourceTimeGridDay");
      if (k === "w") setView("timeGridWeek");
      if (k === "m") setView("dayGridMonth");
      if (k === "y") setView("multiMonthYear"); 
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentDate]);

  /* ==============================
      FullCalendar 이벤트 변환
  ============================== */
  const fcEvents = useMemo(() => {
    const result = [];
    serverEvents.forEach((ev) => {
      const eventType = ev.event_type || "업무일정";
      const isAllDay = eventType === "공지사항";
      const { start_date, end_date, start_time, end_time } = ev;
      const endDateStr = end_date || start_date;

      if (!isAllDay && start_time && end_time && start_date !== endDateStr) {
        const cur = new Date(start_date);
        const last = new Date(endDateStr);
        while (cur <= last) {
          const d = cur.toISOString().slice(0, 10);
          result.push({
            id: `${ev.id}-${d}`,
            title: ev.title,
            start: `${d}T${start_time}`,
            end: `${d}T${end_time}`,
            allDay: false,
            backgroundColor: "transparent",
            borderColor: "transparent",
            resourceId: ev.assignee_display || ev.author_display || "기타",
            extendedProps: {
              color: eventColors[eventType] || "#90caf9",
              assignee_display: ev.assignee_display,
              description: ev.description,
              position_rank: ev.position_rank ?? 999,
              raw: ev,
            },
          });
          cur.setDate(cur.getDate() + 1);
        }
      } else {
        result.push({
          id: String(ev.id),
          title: ev.title,
          start: start_time ? `${start_date}T${start_time}` : start_date,
          end: end_time ? `${endDateStr}T${end_time}` : endDateStr,
          allDay: isAllDay,
          backgroundColor: "transparent",
          borderColor: "transparent",
          resourceId: ev.assignee_display || ev.author_display || "기타",
          extendedProps: {
            color: eventColors[eventType] || "#90caf9",
            assignee_display: ev.assignee_display,
            description: ev.description,
            position_rank: ev.position_rank ?? 999,
            raw: ev,
          },
        });
      }
    });
    return result;
  }, [serverEvents]);

  // ✅ 공휴일 Map (날짜 조회용)
  const holidayMap = useMemo(() => {
    const m = new Map();
    holidays.forEach((h) => m.set(h.date, h));
    return m;
  }, [holidays]);

  // ✅ 공휴일 FullCalendar 이벤트 변환
  const holidayEvents = useMemo(() => {
    return holidays.map((h) => ({
      id: `holiday-${h.date}`,
      title: h.name,
      start: h.date,
      allDay: true,
      backgroundColor: "transparent",
      borderColor: "transparent",
      extendedProps: {
        isHoliday: true,
        holiday_type: h.holiday_type,
      },
    }));
  }, [holidays]);



  /* ==============================
      일정 클릭 / 생성
  ============================= */
  const handleDateClick = (info) => {
    setView("timeGridDay");
    setCurrentDate(info.date);
    setSelectedDate(info.date);
    calRef.current?.getApi()?.gotoDate(info.date);
  };

  const handleEventClick = useCallback((info) => {
    setSelectedEvent(info.event.extendedProps.raw);
    setShowDetailModal(true);
  }, []);

  // ✅ 시간대·날짜 드래그 → 그 범위로 등록 창(2026-10-19). 클릭 한 번은 지금처럼 dateClick(그 날 보기)만 —
  // selectMinDistance로 드래그일 때만 여기로 온다.
  const handleSelect = (info) => {
    calRef.current?.getApi()?.unselect();
    openCreate(rangeFromSelection(info));
  };

  const handleCreateSubmit = async (formData) => {
    // ✅ POST만 수행 (모달 닫기/새로고침은 onClose에서 일괄 처리)
    await api.post("/calendar/events/", formData);
  };


  /* ==============================
      일정 검색 — 결과 클릭 → 그 날짜로 이동 + 강조
  ============================== */
  const handleSearchPick = (r) => {
    if (!preSearchRef.current) preSearchRef.current = { date: currentDate, view };
    // 보기 모드는 유지한다. 단, 연(Y)은 개별 일정을 강조할 수 없어 일(D)로 바꾼다.
    // 일(D) 직원 열이 없는 담당자(퇴직자 등 — resources는 재직·승인 직원만)는
    // 같은 "일" 모드의 일반 일간 보기로 연다(열이 없으면 일정이 그려지지 않는다).
    let next = view === "multiMonthYear" ? "resourceTimeGridDay" : view;
    if (next === "resourceTimeGridDay"
        && !allUsers.some((u) => `${u.name} ${u.position}` === r.assignee_display)) {
      next = "timeGridDay";
    }
    const [y, m, d] = String(r.start_date).split("-").map(Number);
    const date = new Date(y, m - 1, d);
    setHighlightId(r.id);
    setView(next);
    setCurrentDate(date);
    setSelectedDate(date);
    calRef.current?.getApi()?.gotoDate(date);
  };

  // ✅ 다른 화면에서 일정으로 건너오기(2026-10-19, 에어컨 청소 예약 목록의 [일정 보기]).
  // navigate("/etc/calendar/general", { state: { calendarFocus: {id, start_date, assignee_display} } })
  // → 검색 결과를 고른 것과 똑같이 그 날짜로 가서 강조한다. 탭 유지(keep-alive)라 이미 떠 있는
  // 캘린더도 location이 바뀌면 다시 읽으므로, 같은 이동(location.key)은 한 번만 처리한다.
  const location = useLocation();
  const focusKeyRef = useRef(null);
  const searchPickRef = useRef(handleSearchPick);
  searchPickRef.current = handleSearchPick;
  useEffect(() => {
    const focus = location.state?.calendarFocus;
    if (!focus?.id || !focus.start_date || focusKeyRef.current === location.key) return;
    focusKeyRef.current = location.key;
    searchPickRef.current(focus);
  }, [location.key, location.state]);

  const handleSearchClear = () => {
    setHighlightId(null);
    const prev = preSearchRef.current;
    preSearchRef.current = null;
    if (!prev) return;
    setView(prev.view);
    setCurrentDate(prev.date);
    setSelectedDate(prev.date);
    calRef.current?.getApi()?.gotoDate(prev.date);
  };

  // 강조된 일정이 그려지면 화면 가운데로 스크롤(시간 그리드는 그 시각으로 먼저 이동)
  useEffect(() => {
    if (highlightId == null) return undefined;
    const t = setTimeout(() => {
      const hit = serverEvents.find((ev) => ev.id === highlightId);
      if (hit?.start_time) calRef.current?.getApi()?.scrollToTime(String(hit.start_time).slice(0, 5));
      document.querySelector(".search-hit")?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 120);
    return () => clearTimeout(t);
  }, [highlightId, serverEvents, view]);

  /* ==============================
      네비게이션
  ============================== */
  const handlePrev = () => {
    const current = calRef.current?.getApi()?.getDate() || currentDate;
    let newDate;
    if (view === "dayGridMonth") {
      newDate = new Date(current.getFullYear(), current.getMonth() - 1, 1);
    } else if (view === "timeGridWeek") {
      newDate = new Date(current.getFullYear(), current.getMonth(), current.getDate() - 7);
    } else if (view === "multiMonthYear") {
      newDate = new Date(current.getFullYear() - 1, 0, 1);
    } else {
      newDate = new Date(current.getFullYear(), current.getMonth(), current.getDate() - 1);
    }
    setCurrentDate(newDate);
    setSelectedDate(newDate);
    calRef.current?.getApi()?.gotoDate(newDate);
  };
  
  const handleNext = () => {
    const current = calRef.current?.getApi()?.getDate() || currentDate;
    let newDate;
    if (view === "dayGridMonth") {
      newDate = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    } else if (view === "timeGridWeek") {
      newDate = new Date(current.getFullYear(), current.getMonth(), current.getDate() + 7);
    } else if (view === "multiMonthYear") {
      newDate = new Date(current.getFullYear() + 1, 0, 1);
    } else {
      newDate = new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1);
    }
    setCurrentDate(newDate);
    setSelectedDate(newDate);
    calRef.current?.getApi()?.gotoDate(newDate);
  };

  const handleToday = () => {
    const api = calRef.current?.getApi();
    api?.today();
    const d = new Date();
    setCurrentDate(d);
    setSelectedDate(d);
  };
  
  // ✅ 일간 뷰에서 현재 보는 날짜가 공휴일인지
  const viewingDateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
  
  
  const viewingHoliday =
    (view === "resourceTimeGridDay" || view === "timeGridDay")
      ? holidayMap.get(viewingDateStr)
      : null;
  const isViewingHoliday = !!viewingHoliday;

  /* ==============================
      렌더링
  ============================== */
  return (
    <div className={styles.container}>
      {/* === 왼쪽 사이드바 === */}
      <div className={styles.sidebar}>
        <div className={styles.title}><h1>캘린더</h1></div>

        <div className={styles.createButton}>
          <button className={styles.createBtn} onClick={() => openCreate()}>
            <span className={styles.plusIcon}>＋</span> 새 일정 만들기
          </button>
        </div>

        {/* 필터 */}
        <CalendarSidebar
          date={currentDate}
          selectedDate={selectedDate}
          onDateChange={(d) => {
            setSelectedDate(d);
            setCurrentDate(d);
            calRef.current?.getApi()?.gotoDate(d);
          }}
          filters={filters}
          onFiltersChange={setFilters}
          hideHeader={false}
          refreshTrigger={refreshTrigger}
        />

        {loading && <p style={{ color: "#888" }}>불러오는 중…</p>}
        {error && <p style={{ color: "crimson" }}>{error}</p>}
      </div>

      {/* === 오른쪽 본문 === */}
      <div className={styles.mainPanel}>
        <div className={styles.viewControls}>
          <button
            className={`${styles.viewBtn} ${view === "resourceTimeGridDay" ? styles.active : ""}`}
            onClick={() => setView("resourceTimeGridDay")}
          >
            일(D)
          </button>
          <button
            className={`${styles.viewBtn} ${view === "timeGridWeek" ? styles.active : ""}`}
            onClick={() => setView("timeGridWeek")}
          >
            주(W)
          </button>
          <button
            className={`${styles.viewBtn} ${view === "dayGridMonth" ? styles.active : ""}`}
            onClick={() => setView("dayGridMonth")}
          >
            월(M)
          </button>
          <button
            className={`${styles.viewBtn} ${view === "multiMonthYear" ? styles.active : ""}`}
            onClick={() => setView("multiMonthYear")}
          >
            연(Y)
          </button>
          <CalendarSearch onPick={handleSearchPick} onClear={handleSearchClear} />
        </div>

        {/* FullCalendar */}
        <div className={`${styles.calendarContent} ${isViewingHoliday ? "day-holiday-bg" : ""}`}>

          {/* ✅ 일간 뷰 공휴일 배너 */}
          {viewingHoliday && (
            <div style={{
              padding: "8px 12px",
              backgroundColor: "#ffcccc",
              borderBottom: "1px solid #ffa1a1",
              color: "#d32f2f",
              fontWeight: 700,
              fontSize: "14px",
              textAlign: "center",
            }}>
              🇰🇷 {viewingHoliday.name}
              <span style={{ fontSize: "12px", fontWeight: 400, marginLeft: "6px", color: "#b71c1c" }}>
                ({viewingHoliday.holiday_type})
              </span>
            </div>
          )}

          <FullCalendar
            ref={calRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin, resourceTimeGridPlugin]}
            initialView="resourceTimeGridDay"
            headerToolbar={false}
            events={[...fcEvents, ...holidayEvents]}
            resources={resources}
            resourceOrder="ended_order,position_rank"
            locale="ko"
            height="100%"
            eventContent={renderEventContent}
            eventClassNames={(arg) =>
              highlightId != null && arg.event.extendedProps.raw?.id === highlightId ? ["search-hit"] : []}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            selectable={true}
            selectMinDistance={5}
            select={handleSelect}
            allDayContent={() => (
              <span style={{ whiteSpace: "nowrap", fontSize: "13px", fontWeight: "500" }}>
                공지사항
              </span>
            )}
            slotMinTime="06:00:00"
            slotMaxTime="24:00:00"
            slotDuration="00:15:00"
            expandRows={true}
            slotEventOverlap={false}
            slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
            slotLabelContent={(arg) => {
              const hour = arg.date.getHours().toString().padStart(2, "0");
              const minute = arg.date.getMinutes().toString().padStart(2, "0");
              return `${hour}:${minute}`;
            }}

            eventOrder={(a, b) => {
              const rankA = a.extendedProps.position_rank ?? 999;
              const rankB = b.extendedProps.position_rank ?? 999;
              return rankA - rankB;
            }}

            // ✅ 월/연 보기에서 날짜 셀 안에 공휴일명 표기 (시차 에러 보완 버전)
            dayCellContent={(arg) => {
              const year = arg.date.getFullYear();
              const month = String(arg.date.getMonth() + 1).padStart(2, "0");
              const day = String(arg.date.getDate()).padStart(2, "0");
              const dateStr = `${year}-${month}-${day}`;
              
              const holiday = holidayMap.get(dateStr);
              return (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                  <span>{arg.dayNumberText}</span>
                  {holiday && (
                    <span style={{
                      fontSize: "10px",
                      color: "#d32f2f",
                      fontWeight: 600,
                      lineHeight: 1,
                      marginTop: 2,
                    }}>
                      {holiday.name}
                    </span>
                  )}
                </div>
              );
            }}

            // ✅ 월간 셀에 공휴일 클래스 추가 (시차 보완)
            dayCellClassNames={(arg) => {
              const year = arg.date.getFullYear();
              const month = String(arg.date.getMonth() + 1).padStart(2, "0");
              const day = String(arg.date.getDate()).padStart(2, "0");
              const dateStr = `${year}-${month}-${day}`;
              
              return holidayMap.has(dateStr) ? ["is-holiday"] : [];
            }}

            // ✅ 일간/주간 뷰 상단 헤더 영역 공휴일 클래스 부여 (시차 보완)
            dayHeaderClassNames={(arg) => {
              const year = arg.date.getFullYear();
              const month = String(arg.date.getMonth() + 1).padStart(2, "0");
              const day = String(arg.date.getDate()).padStart(2, "0");
              const dateStr = `${year}-${month}-${day}`;
              
              return holidayMap.has(dateStr) ? ["is-holiday"] : [];
            }}


            // ✅ [추가] 안전한 상태 싱크를 위한 datesSet 처리 추가
            datesSet={(info) => {
              if (info.view.type === view) {
                // 불필요한 날짜 객체 재생성 및 이펙트 오작동 방지용 안전 검사
                const targetTime = info.view.currentStart.getTime();
                if (currentDate.getTime() !== targetTime) {
                  setCurrentDate(info.view.currentStart);
                }
              }
            }}
          />
        </div>
      </div>

      {/* === 모달 === */}


      <EventCreateModal
        isOpen={showCreateModal}
        initialRange={createRange}
        onClose={() => {
          setShowCreateModal(false);
          fetchEvents();                 // ✅ 닫힐 때 한 번만 새로고침
          setRefreshTrigger((p) => p + 1);
        }}
        onSubmit={handleCreateSubmit}
      />


      <EventDetailModal
        isOpen={showDetailModal}
        event={selectedEvent}
        onClose={() => setShowDetailModal(false)}
        onDelete={(deletedId) => {
          setServerEvents((prev) => prev.filter((ev) => ev.id !== deletedId));
          setShowDetailModal(false);
          alert("🗑️ 삭제되었습니다.");
          console.log("🗑️ PC: 삭제 즉시 반영됨", deletedId);
        }}
        onUpdate={(updatedEvent) => {
          setServerEvents((prev) =>
            prev.map((ev) => (ev.id === updatedEvent.id ? updatedEvent : ev))
          );
          setRefreshTrigger((p) => p + 1);
          setShowDetailModal(false);
          alert("✅ 수정되었습니다.");
          console.log("✅ PC: 수정 즉시 반영됨", updatedEvent);
        }}
      />
    </div>
  );
}